import clsx from 'clsx'
import React from 'react'

import styleClasses from './SvgIcon.module.css'

type ColorVariant = 'inherit' | 'primary' | 'secondary' | 'action' | 'disabled' | 'error'
type FontSizeVariant = 'inherit' | 'default' | 'small' | 'large'

export type ClasslessSvgIconProps = JSX.IntrinsicElements['svg'] & {
  /**
   * Node passed into the SVG element.
   */
  children?: React.ReactNode
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   * You can use the `htmlColor` prop to apply a color attribute to the SVG element.
   */
  color?: ColorVariant
  /**
   * The fontSize applied to the icon. Defaults to 24px, but can be configure to inherit font size.
   */
  fontSize?: FontSizeVariant
  /**
   * Applies a color attribute to the SVG element.
   */
  htmlColor?: string
  /**
   * The shape-rendering attribute. The behavior of the different options is described on the
   * [MDN Web Docs](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/shape-rendering).
   * If you are having issues with blurry icons you should investigate this prop.
   * @document
   */
  shapeRendering?: string
  /**
   * Provides a human-readable title for the element that contains it.
   * https://www.w3.org/TR/SVG-access/#Equivalent
   */
  titleAccess?: string
  /**
   * Allows you to redefine what the coordinates without units mean inside an SVG element.
   * For example, if the SVG element is 500 (width) by 200 (height), and you pass viewBox="0 0 50 20",
   * this means that the coordinates inside the SVG will go from the top left corner (0,0)
   * to bottom right (50,20) and each unit will be worth 10px.
   */
  viewBox?: string
}

type Props = ClasslessSvgIconProps & {
  classes?: Partial<Record<`color_${ColorVariant}` | `font_size_${FontSizeVariant}` | 'root', string>>
}

const SvgIcon = React.forwardRef<SVGSVGElement, Props>(function SvgIcon(props, ref) {
  const {
    children,
    classes: classesProp,
    className: classNameProp,
    color = 'inherit',
    fontSize = 'default',
    htmlColor,
    titleAccess,
    viewBox = '0 0 24 24',
    ...other
  } = props
  const colorClass = color ? styleClasses[`color_${color}`] : undefined
  const fontSizeClass = fontSize ? styleClasses[`font_size_${fontSize}`] : undefined

  const className = clsx(
    styleClasses.root,
    classesProp?.root,
    colorClass,
    classesProp?.[`color_${color}`],
    fontSizeClass,
    classesProp?.[`font_size_${fontSize}`],
    classNameProp,
  )

  return (
    <svg
      ref={ref}
      aria-hidden={titleAccess ? undefined : true}
      className={className}
      color={htmlColor}
      focusable="false"
      role={titleAccess ? 'img' : undefined}
      viewBox={viewBox}
      {...other}
    >
      {children}
      {titleAccess ? <title>{titleAccess}</title> : null}
    </svg>
  )
})

export type {Props as SvgIconProps}
export {SvgIcon}
